import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Input,
  FormGroup,
  Label,
  Button,
  Table,
  Card,
  CardBody,
  CardImg,
  CardTitle,
  Alert, // Added for notifications
  ListGroup,
  ListGroupItem
} from "reactstrap";
import { DataContext } from "../context/DataContext";
import placeholder from "assets/img/placeholder.jpg";
import { createOrder, updateOrder } from "../context/api"; // Added updateOrder
import { s3ImageUrl } from "../utils/s3Url"; // Import s3ImageUrl

function getLocalDateTimeString() {
  const now = new Date();
  const tzOffsetMs = now.getTimezoneOffset() * 60 * 1000;
  const local = new Date(now.getTime() - tzOffsetMs);
  return local.toISOString().slice(0, 16);
}

function toLocalDateTimeInputValue(value) {
  if (!value) return getLocalDateTimeString();

  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    const tzOffsetMs = date.getTimezoneOffset() * 60 * 1000;
    const local = new Date(date.getTime() - tzOffsetMs);
    return local.toISOString().slice(0, 16);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00`;
  }

  return getLocalDateTimeString();
}

const AddNewOrderModal = ({
  isOpen,
  toggle,
  onSave,
  defaultCustomer = "",
  previousOrderId = null,
  existingOrder = null,
}) => {
  const { data } = useContext(DataContext);
  const customers = data.customers || [];
  const distributions = data.distributions || [];
  const products = data.products || [];
  const isEditMode = Boolean(existingOrder?.orderId);
  const [notes, setNotes] = useState("");
  const [pengellyNotes, setPengellyNotes] = useState(""); // Delivery Instructions for Pengellys
  const [pickingInstructions, setPickingInstructions] = useState(""); // Picking Instructions for Pengellys
  const [sendNoteToPengellys, setSendNoteToPengellys] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [storeNumber, setStoreNumber] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const isSuperuser = data.userProfile?.is_superuser === true;
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(defaultCustomer);
  const [orderId, setOrderId] = useState(""); // This is the Purchase Order #
  const [orderDate, setOrderDate] = useState(getLocalDateTimeString);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedDistribution, setSelectedDistribution] = useState("");
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderQuantity, setOrderQuantity] = useState("");
  const [bonusQuantity, setBonusQuantity] = useState("");
  const [defaultPrice, setDefaultPrice] = useState(0);
  const [customPrice, setCustomPrice]   = useState("");
  const [selectedPriceKey, setSelectedPriceKey] = useState("tier1");
  const [defaultPrices, setDefaultPrices] = useState({});
  const [apiCallStatus, setApiCallStatus] = useState({ type: '', message: '', loading: false });
  // Delivery fee toggle (flat amount in dollars)
  const DELIVERY_FEE_DEFAULT = 25;
  const [addDeliveryFee, setAddDeliveryFee] = useState(false);

  const categories = data.categories || [];
  const brands = data.brands || [];

  const sortedOrderItems = useMemo(
    () => orderItems.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [orderItems]
  );

  const hydrateFormFromOrder = (order) => {
    const customer = customers.find((item) => item.id === order.customerId);
    const hydratedItems = (order.items || [])
      .filter(
        (item) =>
          item?.productId !== "delivery_fee" &&
          item?.type !== "deliveryFee" &&
          item?.isDeliveryFee !== true
      )
      .map((item) => {
        const product = products.find((entry) => entry.id === item.productId) || {};
        const defaultPrices = product.defaultPrices || {};
        const basePrice =
          item.unitPriceCents != null
            ? Number(item.unitPriceCents) / 100
            : 0;
        const bespokePrice =
          item.bespokePriceCents != null
            ? Number(item.bespokePriceCents) / 100
            : null;
        const selectedPriceKey =
          item.productType ||
          Object.entries(defaultPrices).find(
            ([, value]) => Number(value) === basePrice
          )?.[0] ||
          Object.keys(defaultPrices)[0] ||
          "tier1";
        const quantity = Number(item.quantity || 0);
        const total =
          item.totalPriceCents != null
            ? (Number(item.totalPriceCents) / 100).toFixed(2)
            : (quantity * (bespokePrice ?? basePrice)).toFixed(2);

        return {
          ...product,
          id: item.productId,
          productId: item.productId,
          name: item.itemName || product.name || item.name || "(unknown)",
          unitOfMeasure: item.unitOfMeasure || product.unitOfMeasure || "Unit",
          orderQuantity: quantity,
          bonusQuantity: Number(item.bonusQuantity || 0),
          defaultPrice: basePrice,
          selectedPriceKey,
          customPrice: bespokePrice ?? basePrice,
          total,
        };
      });

    setHasAttemptedSubmit(false);
    setValidationErrors({});
    setSelectedCustomer(order.customerId || "");
    setCustomerSearch(customer?.businessName || "");
    setShowCustomerSuggestions(false);
    setSelectedDistribution(order.distribution || customer?.distribution || "");
    setStoreNumber(order.storeNumber || "");
    setOrderId(order.purchaseOrder || "");
    setOrderDate(toLocalDateTimeInputValue(order.date));
    setOrderItems(hydratedItems);
    setSelectedProduct(null);
    setOrderQuantity("");
    setBonusQuantity("");
    setDefaultPrice(0);
    setCustomPrice("");
    setNotes(order.notes || "");
    setPengellyNotes("");
    setPickingInstructions("");
    setSendNoteToPengellys(false);
    setSearchQuery("");
    setSelectedCategory("");
    setSelectedBrand("");
    setAddDeliveryFee(order.delivery === true);
  };

  // const images = require.context("assets/img", true, /\.(png|jpe?g|svg)$/);
  // const getImageUrl = (url) => {
  //   if (!url) return placeholder;
  //   let path = url.replace(/^\//, '').replace(/^assets\/img\//, '');
  //   try { return images(`./${path}`); } catch { return placeholder; }
  // };

  const resetFormStates = () => {
    setHasAttemptedSubmit(false);
    setOrderId("");
    setOrderDate(getLocalDateTimeString());
    setSelectedCustomer(defaultCustomer);
    setOrderItems([]);
    setSelectedProduct(null);
    setOrderQuantity("");
    setBonusQuantity("");
    setDefaultPrice(0);
    setCustomPrice("");
    setNotes("");
    setPengellyNotes("");
    setSendNoteToPengellys(false);
    setPickingInstructions("");
    setSearchQuery("");
    setStoreNumber("");
    setSelectedCategory("");
    setSelectedBrand("");
    setValidationErrors({});
    setShowCustomerSuggestions(false);
  setAddDeliveryFee(false);
    // Do not reset apiCallStatus here if you want to show it after modal closes and reopens.
    // Or reset it if the message should clear when the modal is re-rendered for a new order.
    // setApiCallStatus({ type: '', message: '', loading: false }); 
  };

  const handleCancel = () => {
    resetFormStates();
    setApiCallStatus({ type: '', message: '', loading: false }); // Clear status on cancel
    toggle();
  };
  

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setApiCallStatus({ type: "", message: "", loading: false });

    if (isEditMode && existingOrder) {
      hydrateFormFromOrder(existingOrder);
      return;
    }

    resetFormStates();
    setSelectedCustomer(defaultCustomer);

    const cust = customers.find(c => c.id === defaultCustomer);
    if (cust) {
      setCustomerSearch(cust.businessName);
      setSelectedDistribution(cust.distribution || "");
    }
    setShowCustomerSuggestions(false);
  }, [isOpen, defaultCustomer, customers, products, isEditMode, existingOrder]);

   const productsSubtotal = orderItems
     .reduce((sum, item) => sum + parseFloat(item.orderQuantity || 0) * parseFloat(item.customPrice || 0), 0);
   const overallTotal = (productsSubtotal + (addDeliveryFee ? DELIVERY_FEE_DEFAULT : 0)).toFixed(2);

  // Calculate unit of measure totals
  const unitOfMeasureTotals = useMemo(() => {
    const totals = {};
    orderItems.forEach(item => {
      const unitType = item.unitOfMeasure || 'Unit';
      const totalQty = (parseInt(item.orderQuantity || 0, 10)) + (parseInt(item.bonusQuantity || 0, 10));
      totals[unitType] = (totals[unitType] || 0) + totalQty;
      console.log('Item unit type:', unitType, 'qty:', totalQty, 'item:', item); // Debug log
    });
    console.log('Final totals:', totals); // Debug log
    return totals;
  }, [orderItems]);

  // Format unit of measure totals for display (e.g., "5C 2U")
  const formatUnitTotals = (totals) => {
    const caseCount = totals.Case || 0;
    const unitCount = totals.Unit || 0;
    
    return `${caseCount}C ${unitCount}U`;
  };

  const handleProductSelect = prod => {
    setSelectedProduct(prod);
    setOrderQuantity("");
    setBonusQuantity("");
  
    const prices = prod.defaultPrices || {};
    setDefaultPrices(prices);
    setCustomPrice("");
    const initialKey = Object.keys(prices)[0] || "";
    setSelectedPriceKey(initialKey);
    setDefaultPrice(prices[initialKey] || 0);
  };

  const handleAddToOrder = () => {
    setValidationErrors({});
    const errors = {};
  
    const numOrderQuantity = parseInt(orderQuantity || 0, 10);
    const numBonusQuantity = parseInt(bonusQuantity || 0, 10);

    if (numOrderQuantity <= 0 && numBonusQuantity <= 0) {
      errors.quantity = "Either order quantity or bonus quantity must be greater than zero";
    }
  
    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      return;
    }
  
    const finalPrice =
      customPrice !== "" && !isNaN(parseFloat(customPrice))
        ? parseFloat(customPrice)
        : defaultPrice;
  
    const total = (numOrderQuantity * finalPrice).toFixed(2);
  
    setOrderItems([
      ...orderItems,
      {
        productId: selectedProduct.id,
        ...selectedProduct,
        orderQuantity: numOrderQuantity, // Store as number
        bonusQuantity: numBonusQuantity, // Store as number
        defaultPrice: defaultPrices[selectedPriceKey],
        selectedPriceKey,
        customPrice: finalPrice,
        total
      }
    ]);
  
    setSelectedProduct(null);
    setOrderQuantity("");
    setBonusQuantity("");
    setCustomPrice("");
  };
  
 const handleRemoveItem = productId =>
   setOrderItems(items =>
     items.filter(item => item.productId !== productId)
   );
  
  const handleSave = async () => {
    setHasAttemptedSubmit(true);
    setApiCallStatus({ type: '', message: '', loading: true });


      // 1) TERRITORY CHECK
    const customerDetails = customers.find(c => c.id === selectedCustomer);

           // ─── ADDRESS CHECK ────────────────────────────────────────
   if (!customerDetails?.address) {
     setApiCallStatus({
       type: 'error',
       message: 'Customer address is missing. Please update the address in Business Details before saving.',
       loading: false
     });
     return;
   }
   let addressArray = [];
   try {
     addressArray = JSON.parse(customerDetails.address);
   } catch (e) {
     // invalid JSON, treat as missing
   }
   const hasValidAddress = Array.isArray(addressArray) && addressArray.some(part => part && part.trim());
   if (!hasValidAddress) {
     setApiCallStatus({
       type: 'error',
       message: 'Customer address is missing. Please update the address in Business Details before saving.',
       loading: false
     });
     return;
   }
   // ────────────────────────────────────────────────────────
    if (!customerDetails?.distribution) {
      setApiCallStatus({
        type: 'error',
        message: 'Customer territory is missing. Please update the territory in Business Details before saving.',
        loading: false
      });
      return;
    }


    
    const currentErrors = {}; // Renamed to avoid conflict with validationErrors state
    if (!selectedCustomer) currentErrors.customer = "Customer is required";
    if (!orderDate) currentErrors.date = "Date is required";
    if (orderItems.length === 0) currentErrors.items = "At least one product must be added";
    // if (!orderId) currentErrors.orderId = "Purchase Order # is required"; // Added PO# validation


    if (Object.keys(currentErrors).length > 0) {
      setValidationErrors(currentErrors);
      setApiCallStatus({ type: 'error', message: 'Please correct validation errors.', loading: false });
      return;
    }
    setValidationErrors({});

    // Build combined notes for internal order
    let combinedInternalNotes = "";
    const trimmedInternalNotes = notes.trim();
    const trimmedDeliveryNotes = pengellyNotes.trim();
    const trimmedPickingNotes = pickingInstructions.trim();

    if (trimmedInternalNotes) {
      combinedInternalNotes += `Order Notes: ${trimmedInternalNotes}`;
    }
    if (sendNoteToPengellys) {
      if (trimmedDeliveryNotes) {
        if (combinedInternalNotes) combinedInternalNotes += "\n\n";
        combinedInternalNotes += `Delivery Instructions: ${trimmedDeliveryNotes}`;
      }
      if (trimmedPickingNotes) {
        if (combinedInternalNotes) combinedInternalNotes += "\n\n";
        combinedInternalNotes += `Picking Instructions: ${trimmedPickingNotes}`;
      }
    }

    const internalOrderPayload = {
      orderId: orderId, 
      date: orderDate,
      customerId: selectedCustomer,
      outletName: customers.find(c => c.id === selectedCustomer)?.businessName || "",
      accountManager: data.userProfile?.name || "",
      storeNumber: storeNumber,
      distribution: selectedDistribution,
      total: overallTotal,
      delivery: addDeliveryFee === true, // flag for backend
      notes: combinedInternalNotes,
      items: [
        ...orderItems.map(item => ({
          productId: item.id,
          name: item.name,
          quantity: item.orderQuantity,
          quantityType: item.unitOfMeasure || "",
          bonusQuantity: item.bonusQuantity,
          priceType: item.selectedPriceKey,
          price: item.customPrice,
          defaultPrice: item.defaultPrice || 0,
          total: item.total
        })),
        ...(addDeliveryFee
          ? [{
              productId: 'delivery_fee',
              name: 'Delivery Fee',
              quantity: 1,
              quantityType: 'Fee',
              bonusQuantity: 0,
              priceType: 'flat',
              price: DELIVERY_FEE_DEFAULT,
              defaultPrice: DELIVERY_FEE_DEFAULT,
              total: DELIVERY_FEE_DEFAULT.toFixed(2)
            }]
          : [])
      ]
    };

    if (isEditMode) {
      // Edits are backend-only updates: no email resend and no Pengellys submit.
      try {
        const updatedOrder = await updateOrder(existingOrder.orderId, internalOrderPayload);
        resetFormStates();
        if (typeof onSave === "function") {
          onSave(updatedOrder);
        } else {
          toggle();
        }
      } catch (err) {
        console.error("Failed to update order:", err);
        setApiCallStatus({
          type: "error",
          message: `Failed to update order: ${err.message}`,
          loading: false,
        });
      }
      return;
    }

    let savedInternalOrder;
    try {
      savedInternalOrder = await createOrder(internalOrderPayload);
      console.log("Internal Order created:", savedInternalOrder);
      setApiCallStatus({ type: 'success', message: 'Order created successfully!', loading: false });
    } catch (err) {
      console.error("Failed to create internal order:", err);
      setApiCallStatus({ type: 'error', message: `Failed to create order: ${err.message}`, loading: false });
      return; // Stop if internal order creation fails
    }

    // --- Pengellys API Interaction ---
    if (!isEditMode && isPengellyDist && savedInternalOrder.orderId) {
      setApiCallStatus(prev => ({ ...prev, message: `${prev.message} Now submitting to Pengellys...`, loading: true }));
      
      const customerDetails = customers.find(c => c.id === selectedCustomer);
      let parsedAddress = ["", "", "", ""]; // Default empty address parts
      if (customerDetails && customerDetails.address) {
        try {
          const addrArray = JSON.parse(customerDetails.address);
          if (Array.isArray(addrArray) && addrArray.length >= 4) {
            parsedAddress = addrArray;
          }
        } catch (e) {
          console.warn("Could not parse customer address for Pengellys:", e);
          // parsedAddress remains as default empty strings
        }
      }

      const pengellysProducts = orderItems.map(item => {
        const productDetail = products.find(p => p.id === item.productId);
        console.log("Product detail for Pengellys:", productDetail);
        return {
          code: productDetail?.code || "N/A",
          orderQty: (item.orderQuantity || 0) + (item.bonusQuantity || 0),
          skuType: productDetail?.unitOfMeasure || "N/A",
          name: item.name
        };
      }); 
      const selectedDist = distributions.find(d => d.name === selectedDistribution) || {};
      const pengellysPayload = {
        apiKey: process.env.REACT_APP_PENGELLY_API_KEY, // Ensure this is set in .env
        transType: "StockOut",
        orderDate: orderDate,
        warehouseID: selectedDist.id,
        orderNo: orderId, 
        supplierOrderNo: savedInternalOrder.orderId, 
        deliveryInstructions: trimmedDeliveryNotes,
        pickingInstructions: trimmedPickingNotes,
        deliverTo: {
          customerEmail: customerDetails?.contactEmail || "",
          customerName: customerDetails?.businessName || "",
          address1: parsedAddress[0] || "",
          address2: parsedAddress[1] || "",
          city: parsedAddress[2] || "",
          postCode: parsedAddress[3] || "",
          customerPhone: customerDetails?.phone || ""
        },
        products: pengellysProducts
      };

      try {
        console.log("Sending Pengellys payload:", pengellysPayload);
        const pengellysResponse = await fetch("https://www.pengellys.co.nz/Apps/bewebmvc/api/SubmitOrder", {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(pengellysPayload)
        });
        
        const pengellysResult = await pengellysResponse.json();
        console.log("Pengellys API response status:", pengellysResult);
        if (pengellysResponse.ok && pengellysResult.success && pengellysResult.pickNo) {
          setApiCallStatus(prev => ({ ...prev, message: `${prev.message} Pengellys submission successful (Pick No: ${pengellysResult.pickNo}). Updating internal order...`, loading: true }));
          try {
            const pickUpdatedOrder = await updateOrder(savedInternalOrder.orderId, { pick: pengellysResult.pickNo });
            resetFormStates();
            if (typeof onSave === "function") {
              onSave(pickUpdatedOrder);
            } else {
              toggle();
            }
          } catch (updateErr) {
            console.error("Failed to update internal order with pick number:", updateErr);
            setApiCallStatus(prev => ({ type: 'warning', message: `Order created. Pengellys Pick No: ${pengellysResult.pickNo}, but failed to save it to internal order: ${updateErr.message}`, loading: false }));
          }
        } else {
          console.error("Pengellys API Error:", pengellysResult.message || "Unknown error");
          setApiCallStatus(prev => ({ type: 'warning', message: `Order created, but Pengellys submission failed: ${pengellysResult.message || 'Unknown error'}`, loading: false }));
        }
      } catch (fetchErr) {
        console.error("Failed to call Pengellys API:", fetchErr);
        setApiCallStatus(prev => ({ type: 'warning', message: `Order created, but Pengellys submission failed: ${fetchErr.message}`, loading: false }));
      }
    } else if (!isEditMode && sendNoteToPengellys && (!savedInternalOrder || !savedInternalOrder.id)) {
        setApiCallStatus({ type: 'error', message: 'Internal order ID missing, cannot submit to Pengellys.', loading: false });
    }


    if (!isPengellyDist) {
      resetFormStates();
      if (typeof onSave === "function") {
        onSave(savedInternalOrder);
      } else {
        toggle();
      }
    }
  };
  
    const isPengellyDist = useMemo(() => {
    const distObj = distributions.find(d => d.name === selectedDistribution);
    return Boolean(distObj?.is_pengelly);
  }, [selectedDistribution, distributions]);



  // Filter grid
  const visibleProducts = products
    .filter(p => !p.archived) // Add this line to filter out archived products
    .filter(p => (!searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase())))
    .filter(p => (!selectedCategory || p.productType === selectedCategory))
    .filter(p => {
      if (!selectedBrand) return true; // No brand filter applied
      const brandFilterObject = brands.find(b => b.name === selectedBrand);
      if (!brandFilterObject) return false; // Selected brand name not found in context (should not happen if dropdown is sourced from context)
      return p.brand && p.brand.id === brandFilterObject.id;
    })


  return (
    <Modal
        isOpen={isOpen}
        toggle={handleCancel} // Use handleCancel for toggle to ensure status cleanup
        size="xl"
        style={{ paddingTop: '2rem' }}
      >
      <ModalHeader toggle={handleCancel}>
        {isEditMode ? `Edit Order #${existingOrder.orderId}` : "Add New Order"}
      </ModalHeader>
      <ModalBody>
        {apiCallStatus.message && (
          <Alert color={apiCallStatus.type === 'success' ? 'success' : apiCallStatus.type === 'warning' ? 'warning' : 'danger'} isOpen={!!apiCallStatus.message}>
            {apiCallStatus.loading && <i className="fa fa-spinner fa-spin" style={{ marginRight: '5px' }}></i>}
            {apiCallStatus.message}
          </Alert>
        )}
        {/* Customer & Date */}
        <Row className="mb-1">
          <Col md="6">
          <FormGroup style={{ position: "relative" }}>
  <Label for="customerSearch">Customer</Label>
  <Input
    type="text"
    value={customerSearch}
    onFocus={() => setShowCustomerSuggestions(true)}
    onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)} // setTimeout to allow click to register
    onChange={e => {
      setCustomerSearch(e.target.value);
      setSelectedCustomer(null); // Clear selection when searching
      setShowCustomerSuggestions(true);
    }}
    placeholder="Search for a customer..."
    className={hasAttemptedSubmit && validationErrors.customer ? "is-invalid" : ""}
  />
  {hasAttemptedSubmit && validationErrors.customer && (
    <div className="invalid-feedback">{validationErrors.customer}</div>
  )}
  {showCustomerSuggestions && customerSearch && (
    <ListGroup style={{ position: "absolute", zIndex: 1000, width: "calc(100% - 30px)" }}>
      {customers
        .filter(c => c.businessName.toLowerCase().includes(customerSearch.toLowerCase()))
        .slice(0, 5)
        .map(c => (
          <div
            key={c.id}
            className="list-group-item list-group-item-action"
            style={{
              cursor: "pointer",
            }}
            onClick={() => {
              setSelectedCustomer(c.id);
              setCustomerSearch(c.businessName);
              setSelectedDistribution(c.distribution || "");
              setShowCustomerSuggestions(false);
            }}
          >
            {c.businessName}
          </div>
        ))}
    </ListGroup>
  )}
</FormGroup>
          </Col>
          <Col md="2">
          <FormGroup>
      <Label for="storeNumber">Store Number</Label>
      <Input
        type="text"
        id="storeNumber"
        value={storeNumber}
        placeholder="Enter store #"
        onChange={e => setStoreNumber(e.target.value)}
      />
    </FormGroup>

          </Col>
          <Col md="2">
  <FormGroup>
    <Label for="purchaseOrderNumber">PO #</Label>
    <Input
      type="text"
      id="purchaseOrderNumber"
      value={orderId}
      onChange={(e) => setOrderId(e.target.value)}
      placeholder="Enter PO #"
      // invalid={!!validationErrors.orderId}
    />
    {/* {validationErrors.orderId && (
  <small className="text-danger d-block">{validationErrors.orderId}</small>
)} */}
  </FormGroup>
</Col>
<Col md="2">
<FormGroup>
              <Label for="orderDate">Date</Label>
              <Input
                type="datetime-local"
                id="orderDate"
                value={orderDate}
                onChange={e => setOrderDate(e.target.value)}
                invalid={!!validationErrors.date}
              />
              {validationErrors.date && (
  <small className="text-danger d-block">{validationErrors.date}</small>
)}
            </FormGroup>

              </Col>
        </Row>

        <Row>
          {/* Filters & Grid */}
          <Col md="7">
          
            
            <FormGroup row>
                <Col md="4">
                  <Label>Category</Label>
                  <Input
                    type="select"
                    value={selectedCategory}
                    onChange={e => setSelectedCategory(e.target.value)}
                  >
                    <option value="">All</option>
                    {categories
                      .filter(c => !c.is_archived)
                      .map(c => (
                        <option key={c.id} value={c.name}>
                          {c.name}
                        </option>
                      ))
                    }
                  </Input>
                </Col>
              <Col md="4">
                <Label>Brand</Label>
                <Input
                  type="select"
                  value={selectedBrand}
                  onChange={e => setSelectedBrand(e.target.value)}
                >
                  <option value="">All</option>
                  {brands
                  .filter(b => !b.is_archived) // Corrected from c.is_archived
                  .map(b => (
                    // Assuming value should be b.name if that's what's stored/used elsewhere (Comment moved)
                    <option key={b.id} value={b.name}> 
                      {b.name}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label for="distributionSelect">Distribution</Label>
                  <Input
                    type="select"
                    id="distributionSelect"
                    value={selectedDistribution}
                    onChange={e => setSelectedDistribution(e.target.value)}
                    disabled={!isSuperuser}
                  >
                    <option value="">Select Distribution</option>
                    {distributions
                      .filter(d => !d.is_archived)
                      .map(d => (
                        <option key={d.name} value={d.name}>
                          {d.name}
                        </option>
                      ))
                    }
                  </Input>
                </FormGroup>
            </Col>
            </FormGroup>
            <Row form className="mb-3">
            <Col md="9"> 
              <FormGroup>
                <Label for="searchProducts">Search Products</Label>
                <Input
                  type="text"
                  id="searchProducts"
                  value={searchQuery}
                  placeholder="Filter by name..."
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </FormGroup>
            </Col>

          </Row>
            <div style={{ maxHeight: 400, overflowY: 'auto', overflowX: 'hidden', paddingRight: '0.5rem'  }}>
              <Row>
                {visibleProducts.map(prod => (
                  <Col md="3" key={prod.id} className="mb-2">
                    <Card
                      onClick={() => handleProductSelect(prod)}
                      style={{ cursor: 'pointer' }}
                      className={selectedProduct?.id === prod.id ? "border-primary" : ""}
                    >
                      <CardImg
                        top
                        width="100%"
                        src={prod.image && prod.image.startsWith('http') ? prod.image : (prod.id ? s3ImageUrl("products", prod.id, ".png") : placeholder)}
                        alt={prod.name}
                        style={{ height: 100, objectFit: 'cover' }}
                        onError={(e) => e.target.src = placeholder} // Add onError fallback
                      />
                      <CardBody className="p-2">
                        <CardTitle tag="h6" className="mb-0" style={{ fontSize: '0.8rem' }}>
                          {prod.name}
                        </CardTitle>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </Col>

          {/* Order Items */}
          <Col md="5">
          {selectedProduct && (
            <div className="mb-4 p-3 border rounded">
              <h5>Adding: {selectedProduct.name}</h5>

<FormGroup>
  <Label>Order Quantity</Label>
  <Input
    type="number"
    min={0}
    value={orderQuantity}
    onChange={e => {
      const val = e.target.value; // Keep as string for controlled input, parse in handler
      setOrderQuantity(val);
    }}
    placeholder="Enter order qty"
    invalid={!!validationErrors.quantity}
  />
</FormGroup>

<FormGroup>
  <Label>Bonus Quantity</Label>
  <Input
    type="number"
    min={0}
    value={bonusQuantity}
     onChange={e => {
      const val = e.target.value; // Keep as string
      setBonusQuantity(val);
    }}
    placeholder="Enter bonus qty"
    invalid={!!validationErrors.quantity}
  />
  {validationErrors.quantity && (
    <small className="text-danger d-block">{validationErrors.quantity}</small>
  )}
</FormGroup>

              <FormGroup>
                  <Label>Price Type</Label>
                  <Input
                    type="select"
                    value={selectedPriceKey}
                    onChange={e => {
                      const key = e.target.value;
                      setSelectedPriceKey(key);
                      const priceValue = defaultPrices[key] || 0;
                      setDefaultPrice(priceValue);
                      setCustomPrice(""); // Reset custom price when price type changes
                    }}
                  >
                    {Object.entries(defaultPrices).map(([key, value]) => (
                      <option key={key} value={key}>
                        {key.charAt(0).toUpperCase() + key.slice(1)} – ${parseFloat(value || 0).toFixed(2)}
                      </option>
                    ))}
                  </Input>
                </FormGroup>

              <FormGroup>
                <Label>Custom Price (override)</Label>
                <Input
                  type="number"
                  value={customPrice}
                  step="0.01"
                  min="0"
                  placeholder={`Default: $${parseFloat(defaultPrice || 0).toFixed(2)}`}
                  onChange={e => {
                    const value = e.target.value;
                    setCustomPrice(value); // Keep as string for controlled input
                  }} // Ensuring this block is correctly closed
                />
              </FormGroup>

              <Button color="success" onClick={handleAddToOrder} block>
                Add to Order
              </Button>
            </div>
          )}


                <div style={{ maxHeight: selectedProduct ? 300 : 550, overflowY: 'auto', overflowX: 'hidden', paddingRight: '0.5rem' }}>
                  <h6>Order Summary</h6>
                  {orderItems.length === 0 && <p className="text-muted">No items added yet.</p>}
                  <Table size="sm" hover responsive>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Bns</th>
                        <th>Price</th>
                        <th>Unit</th>
                        <th>Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedOrderItems.map(item => (
                        <tr key={item.productId}>
                          <td>{item.name}</td>
                          <td>{item.orderQuantity}</td>
                          <td>{item.bonusQuantity || 0}</td>
                          <td>${parseFloat(item.customPrice || 0).toFixed(2)}</td>
                          <td>{item.unitOfMeasure || 'Unit'}</td>
                          <td>${item.total}</td>
                          <td>
                              <Button onClick={() => handleRemoveItem(item.productId)}>X</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                     {orderItems.length > 0 && (
                    <tfoot>
                        {addDeliveryFee && (
                          <tr>
                            <td colSpan="5" className="text-right"><strong>Delivery Fee:</strong></td>
                            <td><strong>${DELIVERY_FEE_DEFAULT.toFixed(2)}</strong></td>
                            <td></td>
                          </tr>
                        )}
                        <tr>
                            <td colSpan="4" className="text-right"><strong>Total:</strong></td>
                            <td><strong>{formatUnitTotals(unitOfMeasureTotals)}</strong></td>
                            <td><strong>${overallTotal}</strong></td>
                            <td></td>
                        </tr>
                    </tfoot>
                    )}
                  </Table>
                </div>
                            {validationErrors.items && (
                  <div className="text-danger text-right mt-2">
                    <small>{validationErrors.items}</small>
                  </div>
                )}
                  {/* ─── NOTES FIELD ──────────────────────────────────────────────── */}
          {/* Delivery Fee Toggle as Button */}
          <Row className="mb-2">
            <Col md={12}>
              <Button
                color={addDeliveryFee ? "success" : "secondary"}
                outline={!addDeliveryFee}
                onClick={() => setAddDeliveryFee(prev => !prev)}
                className="d-inline-flex align-items-center"
                aria-pressed={addDeliveryFee}
              >
                <i className="fa fa-truck" style={{ marginRight: '8px' }}></i>
                {addDeliveryFee ? "Delivery fee added" : "Add delivery fee"} (${DELIVERY_FEE_DEFAULT.toFixed(2)})
              </Button>
            </Col>
          </Row>

          {/* Notes & Send to Pengellys checkbox */}
          <Row className="mb-3 mt-3"> {/* Row for Order Notes only */}
            <Col md={12}> {/* Order Notes takes full width */}
              <FormGroup>
                <Label for="notes">Order Notes (internal only)</Label>
                <Input
                  type="textarea"
                  id="notes"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Enter any order notes for internal use..."
                  rows="2"
                />
              </FormGroup>
            </Col>
          </Row>

          {/* New Row for "Send note to Pengellys" Checkbox, if applicable */}
          {!isEditMode && isPengellyDist && (
            <Row className="mb-2"> {/* Adjusted margin */}
              <Col md={12}>
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={sendNoteToPengellys}
                      onChange={e => setSendNoteToPengellys(e.target.checked)}
                    />
                    <i className="fa fa-envelope" style={{ marginRight: '5px', marginLeft: '5px' }}></i> {/* Added mail icon */}
                    Send order details to Pengellys
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          )}

          {/* Pengellys Note on its own row (as per previous adjustment) */}
          {sendNoteToPengellys && !isEditMode && isPengellyDist && (
            <Row className="mb-3">
              <Col md={12}>
                <FormGroup>
                  <Label for="pengellyNotes">Delivery Instructions (for Pengellys)</Label>
                  <Input
                    type="textarea"
                    id="pengellyNotes"
                    value={pengellyNotes}
                    onChange={e => setPengellyNotes(e.target.value)}
                    placeholder="Enter delivery instructions for Pengellys..."
                    rows="2"
                  />
                </FormGroup>
              </Col>
            </Row>
          )}

          {/* Picking Instructions on its own row */}
          {sendNoteToPengellys && !isEditMode && isPengellyDist && (
            <Row className="mb-3">
              <Col md={12}>
                <FormGroup>
                  <Label for="pickingInstructions">Picking Instructions (for Pengellys)</Label>
                  <Input
                    type="textarea"
                    id="pickingInstructions"
                    value={pickingInstructions}
                    onChange={e => setPickingInstructions(e.target.value)}
                    placeholder="Enter picking instructions for Pengellys..."
                    rows="2"
                  />
                </FormGroup>
              </Col>
            </Row>
          )}

          </Col> {/* End of md-5 for order items and notes */}
        </Row> {/* End of main Row for filters/grid and order items/notes */}
      </ModalBody>
      <ModalFooter>
        <> {/* Outer fragment for all ModalFooter children */}
          {(apiCallStatus.message && !apiCallStatus.loading) ? (
            <Col md="8" className="text-left">
              <Alert color={apiCallStatus.type === 'success' ? 'success' : apiCallStatus.type === 'warning' ? 'warning' : 'danger'} isOpen={!!apiCallStatus.message} className="mb-0 py-1 px-2 small">
                {apiCallStatus.message}
              </Alert>
            </Col>
          ) : (
            <></> // Use an empty fragment for the false case
          )}

          <Button color="secondary" onClick={handleCancel} disabled={apiCallStatus.loading}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSave} disabled={apiCallStatus.loading}>
            {apiCallStatus.loading ? (
              <><i className="fa fa-spinner fa-spin" style={{ marginRight: '5px' }}></i> Saving...</>
            ) : (
              isEditMode ? "Save Changes" : "Save Order"
            )}
          </Button>
        </>
      </ModalFooter>
    </Modal>
  );
};

export default AddNewOrderModal;
